import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isAfter, isToday } from 'date-fns';
import { Form, Formik } from 'formik';
import isBoolean from 'lodash/isBoolean';
import some from 'lodash/some';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import * as Yup from 'yup';
import { ParcelNumberValidationStatus, postValidateParcelNumber } from '@yme/api';
import { InfoOutline } from '@yme/assets/icons';
import { Alert, AlertType } from '@yme/atoms/alert';
import { BrandOverline } from '@yme/atoms/brand-overline';
import { Col, Row } from '@yme/atoms/grid';
import { SVGIcon } from '@yme/atoms/svg-icon';
import { Tooltip, TooltipPlacement } from '@yme/atoms/tooltip';
import { getParcelNumber } from '@yme/components/ReturnForm/helpers';
import { useWindowSize } from '@yme/hooks/index';
import { setIsModalShowed } from '@yme/modals/actions';
import { MODALS } from '@yme/modals/constants';
import { FormField, FormFieldType } from '@yme/molecules/form-field';
import { getRTL } from '@yme/selectors/index';
import { useTranslation } from '@yme/services/i18n';
import { colorAccentError900, colorCoreBlack300, colorCoreBlack900Base, colorCoreWhite1000, defaultTransparent, } from '@yme/tokens/colors';
import { FlexWrapper } from '@yme/tokens/flexTokens';
import { HeadingLevel3, RegularLinkLevel2, RegularTextLevel3 } from '@yme/tokens/typography';
import { asyncDebounce } from '@yme/utils/asyncDebounce';
import { DigitalDataMutation, setDigitalData } from '@yme/utils/common';
import { ButtonsNavBar, DeliverySection, InfoTable } from '../components';
import { FormikFields, FormReturnType, StepRoutes } from '../constants';
import { IconContainerStyled, InputWithIconContainerStyled } from './PackageStepStyled';
import { ContentContainerStyled, FlexWrapperStyled, InfoSectionContainerStyled } from './styled';
export const PackageStep = memo(({ cachedData: { orderDetails, orderDetails: { orderNumber, orderEntries, deliveries }, parcelNumber, pickupDate, isCustomDeliveryAddress: cachedIsCustomDeliveryAddress, pickupAddress, pickupPostalCode, pickupPlace, }, commercetoolsProject, returnType, redirectFormPageUrl, setCachedData, }) => {
    const dispatch = useDispatch();
    const history = useHistory();
    const { t } = useTranslation();
    const { isMobileView } = useWindowSize();
    const isRightToLeft = useSelector(getRTL);
    const formikRef = useRef(null);
    const [isCustomDeliveryAddress, setIsCustomDeliveryAddress] = useState(isBoolean(cachedIsCustomDeliveryAddress) ? cachedIsCustomDeliveryAddress : false);
    const [hasParcelUnit, setHasParcelUnit] = useState(false);
    const checkParcelOnProductType = useCallback((parcelName) => {
        const hasMatchingOrders = some(orderEntries, (order) => order.productType &&
            some(deliveries, ({ items, parcels }) => some(items, {
                id: order.id,
            }) && some(parcels, (parcel) => getParcelNumber(parcel) === parcelName)));
        setHasParcelUnit(hasMatchingOrders);
    }, [deliveries, orderEntries]);
    const handleInputChange = useCallback((value) => {
        if (value) {
            checkParcelOnProductType(value);
        }
    }, [checkParcelOnProductType]);
    const validateParcelNumberField = useMemo(() => asyncDebounce(async (parcelNumber, context) => {
        try {
            const response = await postValidateParcelNumber(orderNumber, parcelNumber, commercetoolsProject);
            if (response?.message === ParcelNumberValidationStatus.EXPIRED) {
                return context.createError({
                    message: t('returnForm_yourOrderExpired_error'),
                });
            }
            return response?.message === ParcelNumberValidationStatus.SUCCESS;
        }
        catch (error) {
            return false;
        }
    }, 500), [orderNumber, commercetoolsProject, t]);
    const formikInitialValues = {
        [FormikFields.PARCEL_NUMBER]: parcelNumber || '',
        [FormikFields.PICK_UP_DATE]: pickupDate,
        [FormikFields.ADDRESS]: pickupAddress || '',
        [FormikFields.POSTAL_CODE]: pickupPostalCode || '',
        [FormikFields.PLACE_CODE]: pickupPlace || '',
    };
    const formikValidationSchema = Yup.object().shape({
        [FormikFields.PARCEL_NUMBER]: Yup.string()
            .required(' ')
            .test('', t('returnForm_yourPackage_error'), function (value) {
            return validateParcelNumberField(value, this);
        }),
        ...(returnType === FormReturnType.COLLECTION && {
            [FormikFields.PICK_UP_DATE]: Yup.date()
                .required(' ')
                .test('', ' ', (value) => value && (isToday(value) || isAfter(value, new Date()))),
            ...(isCustomDeliveryAddress && {
                [FormikFields.ADDRESS]: Yup.string().required(' '),
                [FormikFields.POSTAL_CODE]: Yup.string().required(' '),
                [FormikFields.PLACE_CODE]: Yup.string().required(' '),
            }),
        }),
    });
    useEffect(() => {
        if (!isCustomDeliveryAddress) {
            formikRef.current?.setValues({
                ...formikRef.current?.values,
                [FormikFields.ADDRESS]: '',
                [FormikFields.POSTAL_CODE]: '',
                [FormikFields.PLACE_CODE]: '',
            }, true);
        }
    }, [isCustomDeliveryAddress]);
    useEffect(() => {
        formikRef.current?.validateForm();
    }, [isCustomDeliveryAddress, commercetoolsProject]);
    useEffect(() => {
        setDigitalData(DigitalDataMutation.RETURN_PAGE_PACKAGE_VIEW, null);
    }, []);
    const handleSubmit = useCallback(async ({ [FormikFields.PARCEL_NUMBER]: parcelNumber, [FormikFields.PICK_UP_DATE]: pickupDate, [FormikFields.ADDRESS]: pickupAddress, [FormikFields.POSTAL_CODE]: pickupPostalCode, [FormikFields.PLACE_CODE]: pickupPlace, }) => {
        setCachedData(({ orderDetails, orderNumber, email }) => ({
            orderDetails,
            orderNumber,
            email,
            parcelNumber,
            pickupDate,
            isCustomDeliveryAddress,
            pickupAddress,
            pickupPostalCode,
            pickupPlace,
        }));
        history.push(`/${StepRoutes.SELECT_ITEMS_STEP}`);
    }, [history, isCustomDeliveryAddress, setCachedData]);
    const showMobileInfoModal = useCallback(() => {
        dispatch(setIsModalShowed({
            type: MODALS.ALERT_MESSAGE_MODAL,
            data: {
                title: t('returnForm_yourPackagePackageNumberPopUp_header'),
                content: t('returnForm_yourPackagePackageNumberPopUp_message'),
                primaryButtonContent: 'Got it',
            },
        }));
    }, [dispatch, t]);
    return (React.createElement(Formik, { innerRef: formikRef, initialValues: formikInitialValues, validationSchema: formikValidationSchema, validateOnMount: true, onSubmit: handleSubmit }, ({ isValid }) => {
        return (React.createElement(Form, null,
            React.createElement(FlexWrapperStyled, { flexDirection: "column" },
                React.createElement(ContentContainerStyled, { width: "100%", bgColor: defaultTransparent },
                    React.createElement(Row, { justifyContent: "center" },
                        React.createElement(Col, { lg: 8, bgColor: colorCoreWhite1000 },
                            React.createElement(BrandOverline, { borderSize: "6px", borderColor: colorCoreBlack900Base },
                                React.createElement(InfoSectionContainerStyled, null,
                                    React.createElement(HeadingLevel3, null, t('returnForm_yourPackage_header')),
                                    React.createElement(FlexWrapper, { mt: "8px" },
                                        React.createElement(RegularTextLevel3, { color: colorCoreBlack300 }, returnType === FormReturnType.COLLECTION
                                            ? t('returnForm_yourPackageCollection_description')
                                            : t('returnForm_yourOrder_description'))),
                                    hasParcelUnit && (React.createElement(FlexWrapper, { mt: "32px", flexDirection: "column" },
                                        React.createElement(Alert, { message: t('returnForm_formType_alert'), type: AlertType.ERROR, dataTestId: "ReturnForm-AlertError-formType", isRightToLeft: isRightToLeft },
                                            React.createElement(RegularLinkLevel2, { href: redirectFormPageUrl, color: colorAccentError900 }, t('returnForm_formType_link'))))),
                                    React.createElement(FlexWrapper, { mt: "32px", flexDirection: "column" },
                                        React.createElement(InputWithIconContainerStyled, null,
                                            React.createElement(FormField, { fieldType: FormFieldType.INPUT, id: FormikFields.PARCEL_NUMBER, name: FormikFields.PARCEL_NUMBER, label: t('returnForm_yourPackagePackageNumber_label'), placeholder: t('returnForm_yourPackagePackageNumber_placeholder'), type: "text", onChange: handleInputChange, isRightToLeft: false, isDisabled: false, isRequired: true }),
                                            isMobileView ? (React.createElement(React.Fragment, null,
                                                React.createElement(IconContainerStyled, { isRightToLeft: isRightToLeft, onClick: showMobileInfoModal },
                                                    React.createElement(SVGIcon, { type: InfoOutline })))) : (React.createElement(Tooltip, { placement: TooltipPlacement.ABOVE_RIGHT, title: t('returnForm_yourPackagePackageNumberPopUp_message') },
                                                React.createElement(IconContainerStyled, { isRightToLeft: isRightToLeft },
                                                    React.createElement(SVGIcon, { type: InfoOutline })))))),
                                    returnType === FormReturnType.COLLECTION && (React.createElement(FlexWrapper, { mt: "32px", flexDirection: "column" },
                                        React.createElement(FormField, { fieldType: FormFieldType.DATEPICKER, id: FormikFields.PICK_UP_DATE, name: FormikFields.PICK_UP_DATE, label: t('returnForm_yourPackagePickUpDate_placeholder'), isRightToLeft: false, disabledDays: [
                                                {
                                                    dayOfWeek: [0, 6], // Saturday and Sunday
                                                },
                                            ], isRequired: true })))))))),
                React.createElement(ContentContainerStyled, { width: "100%" },
                    React.createElement(Row, { justifyContent: "center" },
                        React.createElement(Col, { lg: 8, bgColor: colorCoreWhite1000 },
                            React.createElement(InfoSectionContainerStyled, null,
                                React.createElement(HeadingLevel3, null, t('returnForm_packageDetails_header')),
                                React.createElement(FlexWrapper, { mt: "8px", flexDirection: "column" },
                                    React.createElement(RegularTextLevel3, { color: colorCoreBlack300 }, t('returnForm_packageDetails_description'))),
                                React.createElement(FlexWrapper, { mt: "32px", flexDirection: "column" },
                                    React.createElement(InfoTable, { ...orderDetails, isRightToLeft: isRightToLeft })),
                                returnType === FormReturnType.COLLECTION && (React.createElement(FlexWrapper, { mt: "32px", flexDirection: "column" },
                                    React.createElement(DeliverySection, { isCustomDeliveryAddress: isCustomDeliveryAddress, setIsCustomDeliveryAddress: setIsCustomDeliveryAddress }))))))),
                React.createElement(ButtonsNavBar, { isNextDisabled: !isValid || hasParcelUnit }))));
    }));
});
PackageStep.displayName = 'YourPackage_ReturnForm';
